import React from "react";
import { withRouter } from "react-router-dom";
import { Popover, PopoverBody } from "reactstrap";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import FullCarouselModal from "../fullCarouselModal";
import * as activityActions from "../../actions/activity";
import * as profileActions from "../../actions/profile";
import {
  getFormattedCurrency,
  formatDate,
  formatDateISO,
  getSizes,
  appendToken,
  getFormattedNumber,
} from "../utils";
import {
  buildAdditionalInfoSection,
  buildMap,
  buildPaymentCalculator,
  buildLiveMortgageUrlLink,
  buildSalesHistorySection,
  buildDocumentsReportTable,
  buildOpenHouseBar,
  displayListingPrice,
  sourceMLSButton,
  buildUnits,
  buildRoomInformation,
  buildUnitInformation,
  buildLevelInformation,  
} from "../listingDetailsHelpers";
import LoginModal from "../LoginModal";
import ListingButtons from "../listingButtons";
import ListingModalComments from "../listingModalComments";
import PrintButton from "../PrintButton";
import DocumentsButton from "../documentsButton";
import Footer from "../footer";
import { PrivateListingPopover } from "../infoPopover";

import ScrollMenu from "../scrollMenu";
import ReportDetailsImage from "../ReportDetailsImage";
import VoicePad from "../VoicePad";
import ExpiredLink from "../ExpiredLink";
import Loading from "../Loading";
import TopFloatButton from "../TopFloatButton";
import ContactFormModal from "../ContactFormModal";
import { getCookie, setCookie } from "../cookies";

// begin images
import crossbutton from "../../images/icons/cross.svg";
import crossActivebutton from "../../images/icons/cross-active.svg";
import infoIcon from "../../images/svg_icons/info.svg";
import exitIcon from "../../images/svg_icons/exit.svg";

// begin styles
import "../../scss/_listingsFilter.scss";
import "../../scss/_listingDetails.scss";
import "../../scss/_listingCard.scss";
import "../../scss/_visitedDate.scss";
import "../../scss/_activityComment.scss";

import RoomsDetails from "./RoomsDetails";
import ListingInformation from "./ListingInformation";
import { sendSharedLinkContactForm } from "../../services/linkService";
import ListingDetailsGallery from "./ListingDetailsGallery";

class ListingDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      listing: null,
      fullCarouselModal: false,
      commentsBar: false,
      estimatedMonthlyPayment: "",
      crossHover: false,
      disclaimer: false,
      loading: true,
      listingId: "",
      openContactModal: false,
      showLoginModal: false,
      initialImageCarousel: 0,
    };
  }

  componentDidMount() {
    const { listingId } = this.props;
    this.loadListingDetails(listingId);

    window.openWindow = this.openWindow;
    window.addEventListener("scroll", this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.listingId !== nextProps.listingId)
      this.loadListingDetails(nextProps.listingId);
    // always fetches listing initially, but if Interest button clicked the updated listing will be in entities
    else if (
      nextProps.listings &&
      this.state.listing &&
      nextProps.listings[this.props.listingId] &&
      JSON.stringify(nextProps.listings[this.props.listingId]) !==
        JSON.stringify(this.state.listing)
    ) {
      // Activity Id has changed from null to something
      if (
        this.state.listing.ActivityId !==
        nextProps.listings[this.props.listingId].ActivityId
      )
        this.props.activityActions.fetchSingleActivity(
          nextProps.listings[this.props.listingId].ActivityId
        );

      this.setState({
        listing: {
          ...this.state.listing,
          ...{
            ActivityId: nextProps.listings[this.props.listingId].ActivityId,
            InterestLevel:
              nextProps.listings[this.props.listingId].InterestLevel,
            CommentsCount:
              nextProps.listings[this.props.listingId].CommentsCount,
          },
        },
        activityId: nextProps.listings[this.props.listingId].ActivityId,
      });
    } else if (
      nextProps.activities &&
      this.state.listing &&
      nextProps.activities[this.state.listing.ActivityId] &&
      JSON.stringify(nextProps.activities.Comments) !==
        JSON.stringify(this.state.listing.Comments)
    ) {
      this.setState({
        listing: {
          ...this.state.listing,
          ...{
            CommentsCount:
              nextProps.activities[this.state.listing.ActivityId].CommentsCount,
            Comments:
              nextProps.activities[this.state.listing.ActivityId].Comments,
          },
        },
      });
    }
  }

  changeFetchListingDetails(listingId) {
    const { publicPortal, listingActions } = this.props
    const { fetchPublicListingDetailsById, fetchListingDetailsById } = listingActions
    return publicPortal
      ? fetchPublicListingDetailsById(listingId)
      : fetchListingDetailsById(listingId)
  }

  loadListingDetails(listingId) {
    this.setState({ loading: true });
    this.changeFetchListingDetails(listingId)
      .then((response) => {
        if (response.error) return;
        const listing = response.response.entities.Listings[listingId];

        this.setState({ listing: listing, activityId: listing.ActivityId });

        if (listing.ActivityId) {
          this.props.activityActions.fetchSingleActivity(listing.ActivityId);
        }
        if (this.props.onListingLoaded) {
          this.props.onListingLoaded(listing);
        }
      })
      .finally(() => this.setState({ loading: false }));
  }

  // ---------------------------------------------------------
  // begin report infrastructure -----------------------------
  // ---------------------------------------------------------
  buildReport = (listing, sections) => {
	const ignoredSections = ["Overview", "Summary", "Rooms_CARD", "Listing_Details_CARD"];
    if (listing.IsRental) ignoredSections.push("Payment Calculator");
    const shouldRender = (section) => !ignoredSections.includes(section.Title);
    const result = [];
    sections.filter(shouldRender).forEach(
      (section, index) => {
        result.push(this.buildSection(section, index, listing));
        // additional and doc sections coupled together always but represet different rows
        if (section.Title === "Additional Info")
          result.push(buildDocumentsReportTable(listing));
      }
    );
    return result;
  };

  buildSection = (section, index, listing) => {
    const customSection = this.buildCustomSection(section, listing);
    if (customSection) return customSection;

    // is data section
    const columnCount =
      (section.Column1 && section.Column1.length ? 1 : 0) +
      (section.Column2 && section.Column2.length ? 1 : 0) +
      (section.Column3 && section.Column3.length ? 1 : 0);
    // summary goes next to image at top and features has different table format
    const rowClass =
      section.Title === "Summary" ? "row" : "row mg-topbtm-40 txt-algn-lft";
    const fieldClass = "listing-details-row";

    return (
      <div id={section.Title} className={rowClass} key={index}>
        {section.Title && (
          <div className="col-12 mg-topbtm-20">
            <h3>{section.Title}</h3>
          </div>
        )}
        {Object.keys(section).map((prop, i) => {
          return this.buildColumn(section[prop], fieldClass, columnCount, i);
        })}
      </div>
    );
  };

  buildColumn = (column, fieldClass, count, i) => {
    if (i >= count) return null;
    return (
      <div className={this.getColClass(count)} key={i}>
        {column.map((field, j) => {
          return (
            <div className={fieldClass} key={j}>
              <div className="listing-details-key">{field.Label}</div>
              <div className="listing-details-value">
                {this.formatField(field.Value, field.Type)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  buildCustomSection = (section, listing) => {
    const { isMobile, settings } = this.props;
    switch (section.Title) {
      case "Map":
        return buildMap(listing);
      case "Payment Calculator":
        const calculatorTools = listing.AdditionalInfoLinks.find(
          (l) => l.label === "Tools"
        );
        const liveMortgageUrlLink = buildLiveMortgageUrlLink(calculatorTools);
        return buildPaymentCalculator(listing, liveMortgageUrlLink, (value) =>
          this.setState({ estimatedMonthlyPayment: value })
        );
      case "Sales History":
        return buildSalesHistorySection(listing, isMobile);
      case "Additional Info":
        const toolLinks = listing.AdditionalInfoLinks.find(
          (l) => l.label === "Tools"
        );
        const tools = toolLinks
          ? toolLinks.links.filter((l) => l.html !== "")
          : null;
        const reportLinks = listing.AdditionalInfoLinks.find(
          (l) => l.label === "Reports"
        );
        const reports = reportLinks
          ? reportLinks.links.filter((l) => l.html !== "")
          : null;
        return reports || tools
          ? buildAdditionalInfoSection(
              tools,
              reports,
              settings.connectMLSServerURL
            )
          : null;
      case "Unit Information":
      	return buildUnitInformation(section);
      case "Room Information":
        return buildRoomInformation(section);
      case "Source MLS":
        return sourceMLSButton;
      case "Units":
        return buildUnits(section);
      case "Level Information":
        return buildLevelInformation(section);
      case "Gallery":
        return (
          <ListingDetailsGallery
            listing={listing}
            onClick={this.openCarouselImage}
          />
        );
      default:
        return null;
    }
  };

  getColClass = (count) => {
    switch (count) {
      case 1:
        return "col-sm-6";
      case 2:
        return "col-sm-6";
      case 3:
        return "col-sm-4";
      default:
        return "col-sm-6";
    }
  };

  formatField = (value, type) => {
    if (!type) return value;
    switch (type) {
      case "text":
        return value;
      case "decimal":
        return value; //TODO round logic
      case "date":
        return formatDate(value);
      case "currency":
        return getFormattedCurrency(value);
      case "html":
        return <div dangerouslySetInnerHTML={{ __html: value }} />;
      case "number": 
        return getFormattedNumber(value);        
      default:
        return value;
    }
  };

  // --------------------------------------------------------
  // begin page events --------------------------------------
  // --------------------------------------------------------
  scrollToSection = (id) => {
    const { isDesktop, onScrollToSection } = this.props;
    const element = document.getElementById(id);
    if (element) {
      if (onScrollToSection) return onScrollToSection(element); // is modal, use callback

      let yOffset = isDesktop ? -58 : -56; // offset for top navbar
      const hasStickyHeader =
        document.getElementsByClassName("sticky-desktop").length ||
        document.getElementsByClassName("sticky-mobile").length;
      if (hasStickyHeader) yOffset -= isDesktop ? 169 : 61; // desktop fixes address bar too
      // adjust element top when not sticky because we know the sticky header will be added instantly
      const topOffset = isDesktop ? 355 : 140;
      const y =
        (hasStickyHeader
          ? element.getBoundingClientRect().top
          : element.getBoundingClientRect().top - topOffset) +
        window.pageYOffset +
        yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  handleScroll = () => {
    if (this.props.modalHeader) return; // modal doesn't stick

    const jumpTo = document.getElementById("scroll-menu");
    const header = document.getElementById("listing-details-header");
    const headerTop = header.offsetTop;
    const stickyClass = this.props.isDesktop
      ? "sticky-desktop"
      : "sticky-mobile";
    if (window.pageYOffset > headerTop) {
      this.setState({ isSticky: true });
      jumpTo.classList.add(stickyClass);
      header.classList.add(stickyClass);
    } else {
      this.setState({ isSticky: false });
      jumpTo.classList.remove(stickyClass);
      header.classList.remove(stickyClass);
    }
  };

  // copied from connectMLS - used by field type 'html'
  openWindow = (url, target, args, returnWindow) => {
    if (target != null && target.length > 0)
      target = target + "0E6DF31A1A25587894C2826DE1A5F05B";
    if (args) {
      if (args.length === 0) args = "resizable=1";
      else if (
        args.match("resizable=0") == null &&
        args.match("resizable=1") == null
      )
        args += ",resizable=1";
    }
    const shouldAppendToken = new URL(url).host !== window.location.host && !(new URL(url).searchParams.has("token"));
    if(shouldAppendToken) url = appendToken(url);
    const newWindow = window.open(url, target, args);
    if (newWindow) newWindow.focus();
    if (returnWindow) return newWindow;
  };

  toggleModal = (key) => {
    this.setState({ [key]: !this.state[key] });
  };

  buildModal = (key) => {
    if (key === "fullCarouselModal") {
      return (
        <FullCarouselModal
          listing={this.state.listing}
          modalIsOpen={this.state[key]}
          toggle={this.toggleModal}
          selectedItem={this.state.initialImageCarousel}
        />
      );
    }
    return null;
  };

  toggleDisclaimer = () => {
    this.setState((prev) => ({ ...prev, disclaimer: !prev.disclaimer }));
  };

  toggleLoginModal = () => {
    this.setState( {
      showLoginModal: !this.state.showLoginModal,
    });
  };

  openCarouselImage = (index = 0) => {
    this.setState({
      fullCarouselModal: true,
      initialImageCarousel: index,
    });
  };

  render() {
    const { isMobile, isDesktop } = getSizes(580);
    const {
      listing,
      commentsBar,
      documentsBar,
      isSticky,
      estimatedMonthlyPayment,
      disclaimer,
      loading,
      crossHover,
      activityId,
      fullCarouselModal,
      openContactModal,
    } = this.state;
    const {
      settings,
      listingActions,
      modalHeader,
      activities,
      myProfile,
      toggleModal,
      modalIsOpen,
      publicPortal,
      history,
      listingAgent,
      publicId,
      listingID
    } = this.props;
    const isModal = !!this.props.modalHeader;
    let documentsBarIsOpen = documentsBar;
    if (typeof this.props.documentsBar !== "undefined")
      documentsBarIsOpen = this.props.documentsBar;
    if (loading) return <Loading height="calc(100vh - 56px)" />;
    if (!listing) return <ExpiredLink />;

    const printPreviewUrl =
      settings?.connectMLSServerURL +
      `/mls/listings/print/print.jsp?dcids=${listing.Id}&report=${
        settings?.PrintReportPage || "clientfull"
      }&category=${listing.PropertyType}&print_now=true`;
    const emailSentCookie = getCookie(`${publicId}_${listingID}`);

    const sectionOverview = listing.ReportFields.Sections.find(
      ({ Title }) => Title === "Overview"
    );
    const sectionOverviewColumns = [
      ...(sectionOverview?.Column1 ?? []),
      ...(sectionOverview?.Column2 ?? []),
      ...(sectionOverview?.Column3 ?? []),
    ];
    const overview = sectionOverviewColumns?.length ? sectionOverviewColumns?.map(({ Value }) => Value) : [listing.Remarks];

    let sections = listing.ReportFields.Sections;

    if (listing.IsRental) sections = sections.filter(item => item.Title !== 'Payment Calculator');

    /**
     * Return MAP section if there is no LAT/LNG
     */
    if(!listing.Latitude || !listing.Longitude) {
      sections = sections.filter(({Title}) => Title !== 'Map');
    }

    const shouldAddGallerySection = !sections.find(({ Name }) => Name === "Gallery") && listing.ListingPhotosMidsize?.length > 0;

    if (shouldAddGallerySection) sections.push({ Name: "Gallery", Title: "Gallery" });

    const Comments = () => commentsBar ? (
      <div className="comments-bar">
        <div className="comments-bar-header">
          <div>
            <div>Comments</div>
            <button
              className="close dyna-close close_black close_top_15"
              onClick={() => this.toggleModal("commentsBar")}
              onMouseEnter={() => this.setState({ crossHover: true })}
              onMouseLeave={() => this.setState({ crossHover: false })}
            >
              <img
                className="no_background"
                src={crossHover ? crossActivebutton : crossbutton}
                alt="close"
              />
            </button>
          </div>
        </div>
        <div className="activity activity_overflow_auto">
          <ListingModalComments
            listing={listing}
            comments={
              activities[activityId]
                ? activities[activityId].Comments
                : null
            }
            myProfile={myProfile}
          />
        </div>
      </div>
    ) : null

    return (
      <div>
        <div
          id="listing-details-header"
          className="listing-details-header borders"
        >
          <div className="header-address">
            <div>
              <div className="street-price ft-bold txt-algn-lft d-flex align-items-center">
                {`${listing.StreetAddress} `}
                {listing.VisiblePrivateListing && (
                  <React.Fragment>
                    <div className="private-listing-flag">PRIVATE LISTING</div>
                    <PrivateListingPopover id={listing.Id} isModal={isModal} />
                  </React.Fragment>
                )}
              </div>
              <div className="city-state-zip txt-algn-lft">
                {listing.City}, {listing.State} {listing.Zip}
              </div>
              {!isMobile && (
                <span>
                  <ul>
                    {listing.ReportFields.QuickInfo.filter((v) => v.Value)
                      .filter(
                        (o) => !["Listing ID", "Status"].includes(o.Label)
                      )
                      .map((obj, i) => {
                        return <li key={i}>{obj.Value}</li>;
                      })}
                  </ul>
                </span>
              )}
            </div>
            <span className="txt-algn-rt mg-lft-auto">
              <div className="street-price ft-bold">
                {displayListingPrice(listing)}
              </div>
              {estimatedMonthlyPayment && (
                <div className="estimate-container">
                  <div
                    className="btn-link estimate"
                    onClick={() => {
                      this.scrollToSection("Payment Calculator");
                    }}
                  >
                    {isMobile ? "Est: " : "Est. Pymt: "}
                    {estimatedMonthlyPayment}
                  </div>
                  <img
                    id="disclaimer"
                    src={infoIcon}
                    alt="disclaimer"
                    onClick={this.toggleDisclaimer}
                  />
                  <Popover
                    placement="bottom"
                    popperClassName="help-popover"
                    isOpen={disclaimer}
                    target="disclaimer"
                    toggle={this.toggleDisclaimer}
                  >
                    <PopoverBody>
                      <p className="exit-button help-text">
                        This estimated payment is provided for informational
                        purposes only. You should not make any decisions based
                        simply on the information provided.
                        <img
                          src={exitIcon}
                          alt="exit"
                          id="exitButton"
                          className="exit-button"
                          onClick={this.toggleDisclaimer}
                        />
                      </p>
                      <p className="help-text">
                        Values indicated are estimates, may not be accurate, and
                        are only used as a guide. This estimated payment should
                        not be relied upon in any way as the sole source of
                        information.
                      </p>
                      <p className="help-text mb-0">
                        Please consult your financial, tax, or legal advisers
                        for an accurate assessment of your mortgage payments and
                        any advice relating to your personal circumstances.
                      </p>
                    </PopoverBody>
                  </Popover>
                </div>
              )}
              {!estimatedMonthlyPayment && (
                <div style={{ height: "21px", visibility: "hidden" }}>
                  est placedholder
                </div>
              )}
              {!isMobile && (!isModal || isDesktop) && (
                <ListingButtons
                  history={history}
                  publicPortal={publicPortal}
                  listing={listing}
                  listingActions={listingActions}
                  isModal={isModal}
                  commentsBarOpen={commentsBar}
                  onCommentsClick={() => this.toggleModal("commentsBar")}
                />
              )}
              {!isMobile && (!isModal || isDesktop) && (
                <div className="secondary-buttons-container">
                  <DocumentsButton
                    publicPortal={this.props.publicPortal}
                    toggleLoginModal={this.toggleLoginModal}
                    listingId={listing.Id}
                    count={
                      (listing.PublicDocuments &&
                        listing.PublicDocuments.length) ||
                      0
                    }
                    isOpen={documentsBar}
                    onDocumentsClicked={() => this.toggleModal("documentsBar")}
                  />
                  <PrintButton
                    publicPortal={this.props.publicPortal}
                    toggleLoginModal={this.toggleLoginModal}
                    printPreviewUrl={printPreviewUrl}
                  />
                  {modalHeader}
                </div>
              )}
            </span>
          </div>
          {isMobile && (
            <div className="w-100 d-flex justify-content-between">
              <div>
                <ul>
                  {listing.ReportFields.QuickInfo.filter((v) => v.Value)
                    .filter((o) => !["Listing ID", "Status"].includes(o.Label))
                    .map((obj, i) => {
                      return <li key={i}>{obj.Value}</li>;
                    })}
                </ul>
              </div>
            </div>
          )}
          {(isMobile || (isModal && !isDesktop)) && (
            <div className="w-100 d-flex">
              <ListingButtons
                history={history}
                publicPortal={publicPortal}
                listing={listing}
                listingActions={listingActions}
                isModal={isModal}
                commentsBarOpen={commentsBar}
                onCommentsClick={() => this.toggleModal("commentsBar")}
                listingAgent={listingAgent}
              />
              <div className="secondary-buttons-container">
                <DocumentsButton
                  listingId={listing.Id}
                  count={
                    (listing.PublicDocuments &&
                      listing.PublicDocuments.length) ||
                    0
                  }
                  isOpen={documentsBar}
                  onDocumentsClicked={() =>
                    publicPortal
                      ? this.toggleLoginModal()
                      : this.toggleModal("documentsBar")
                  }
                />
                <PrintButton
                  printPreviewUrl={printPreviewUrl}
                  history={history}
                  publicPortal={publicPortal}
                />
                {modalHeader}
              </div>
            </div>
          )}
        </div>
        {isDesktop && isSticky && (
          <ScrollMenu
            data={sections
              .filter((s) => s.Name)
              .map((s) => ({
                name: s.Name,
                title: s.Title,
              }))}
            onSelect={(id) => this.scrollToSection(id)}
          />
        )}
        <div
          className={`${
            modalHeader ? "container-fluid" : "container"
          } listing-details-container`}
        >
          <div className="pd-topbtm-20"></div>
          <Comments />
          {documentsBarIsOpen && (
            <div className="comments-bar documents-bar">
              <div className="comments-bar-header">
                <div>
                  <div>Documents</div>
                  <button
                    className="close dyna-close close_black close_top_15"
                    onClick={() => {
                      if (toggleModal) toggleModal("documentsBar");
                      else this.toggleModal("documentsBar");
                    }}
                  >
                    <img src={crossbutton} alt="close" />
                  </button>
                </div>
              </div>
              <div className="documents-bar_links">
                {listing.PublicDocuments && (
                  <table>
                    <tbody>
                      {listing.PublicDocuments.map((obj, i) => {
                        return (
                          <tr key={i}>
                            <td>
                              <button
                                key={i}
                                className="btn-link"
                                onClick={() =>
                                  window.open(appendToken(obj.Url))
                                }
                              >
                                {obj.Name}
                              </button>
                            </td>
                            <td style={{ width: "100px" }}>
                              {formatDateISO(obj.PostDate)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
          {(!isDesktop || !isSticky) && (
            <div className="row">
              <div className="col">
                <ScrollMenu
                  alignCenter={false}
                  data={sections
                    .filter((s) => s.Name)
                    .map((s) => ({ name: s.Name, title: s.Title }))}
                  onSelect={(id) => this.scrollToSection(id)}
                />
              </div>
            </div>
          )}
          <ReportDetailsImage
            listing={listing}
            onClick={() => this.toggleModal("fullCarouselModal")}
            buildOpenHouseBar={buildOpenHouseBar}
            isDesktop={isDesktop}
            publicPortal={publicPortal}
            onContactAgent={() =>
              publicPortal
                ? this.toggleLoginModal()
                : this.setState({ openContactModal: true })
            }
            showNotification={this.props.showNotification}
            listingDetails={
              <>
                <RoomsDetails listing={listing} />
                <ListingInformation listing={listing} />
              </>
            }
          />
          {listing.VoicePadStreamKey && (
            <VoicePad voicePadStreamKey={listing.VoicePadStreamKey} />
          )}
          <div className="row">
            <div className="col mg-top-30 txt-algn-lft">
              {overview.map((item, index) => (
                <p key={index}>{item}</p>
              ))}
            </div>
          </div>
          {this.buildReport(listing, sections)}
          <div className="row">
            <div className="col">
              <Footer settings={settings} />
            </div>
          </div>
          {fullCarouselModal && this.buildModal("fullCarouselModal")}
        </div>
        {!modalIsOpen && isSticky && (
          <>
            <div className="backToTop">
              <TopFloatButton
                onClick={() =>
                  window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                  })
                }
              />
            </div>
          </>
        )}
        {!publicPortal && (
          <ContactFormModal
            open={openContactModal}
            emailSentCookie={
              emailSentCookie && emailSentCookie.includes("true")
            }
            onClose={() => this.setState({ openContactModal: false })}
            onSubmit={({ name, email, phone, message }) =>
              sendSharedLinkContactForm(publicId, listingID, {
                userName: name,
                userEmail: email,
                userPhone: phone,
                writeMessage: message,
                link: window.location.href,
              }).then(() => setCookie(`${publicId}_${listingID}`, true))
            }
          />
        )}
        <LoginModal
          {...this.props}
          open={this.state.showLoginModal}
          onClose={() => this.toggleLoginModal()}
          listingID={this.props.listingId}
          listingAgent={listingAgent}
          fromContact={Boolean(listingAgent)}
        ></LoginModal>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  myProfile: state.profile.mine || {},
  settings: state.profile.settings || {},
  activities: state.entities.Activities || {},
  listings: state.entities.Listings || {},
  listingAgent: state.profile.agent || {},
});

const mapDispatchToProps = (dispatch) => ({
  activityActions: bindActionCreators(activityActions, dispatch),
  profileActions: bindActionCreators(profileActions, dispatch),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ListingDetails)
);
